import type {
  AccountActivitySummary,
  AdminUserSummary,
  ApplicationSettings,
  ApiKeySummary,
  BacktestResult,
  CreateBacktestRequest,
  ContactRequest,
  MarketDataCoverageResponse,
  OrderBook,
  OrderSummary,
  PlaceOrderRequest,
  PortfolioHistoryPoint,
  PositionSummary,
  PredictionMarket,
  SuggestedMarket,
  StrategyDefinitionV2,
  StrategyGenerationUsage,
  UpdateApiKeyRequest,
  UserSummary,
  WalletConfig,
  WalletSummary,
} from "@papertiger/contracts";

/**
 * Browser client for the public PaperTiger HTTP API.
 *
 * Keep `apps/web/public/api.md` updated whenever this client adds, removes, or
 * changes an endpoint, request, response, authentication rule, or API base URL.
 */

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? "https://api.papertigerapp.com";

/** Lightweight wallet list item returned before mark-to-market. */
export interface WalletListItem {
  /** Stable wallet identifier. */
  id: string;
  /** Wallet name. */
  name: string;
  /** Starting cash. */
  startingBalanceDollars: number;
  /** Current cash. */
  cashBalanceDollars: number;
  /** Cash reserved for accepted buy orders. */
  reservedCashDollars: number;
  /** Cash available for new orders. */
  buyingPowerDollars: number;
  /** Wallet settings. */
  config: WalletConfig;
  /** Creation time. */
  createdAt: string;
}

/** Marked portfolio response. */
export interface PortfolioResponse {
  /** Wallet equity summary. */
  wallet: WalletSummary;
  /** Marked positions. */
  positions: PositionSummary[];
  /** Non-fatal venue data failures. */
  warnings: string[];
}

/** Optional filters for immutable-trade wallet equity history. */
export interface PortfolioHistoryQuery {
  /** Inclusive ISO 8601 lower bound. */
  start?: string;
  /** Inclusive ISO 8601 upper bound. */
  end?: string;
  /** Maximum chronological observations to return. */
  limit?: number;
}

/** Browser API client. */
export class PaperTigerApi {
  /** Browser sessions are HTTP-only cookies and cannot be inspected by JavaScript. */
  public hasToken(): boolean { return true; }

  /** Clears the deprecated local-storage token during the cookie migration. */
  public setToken(token: string | null): void {
    void token;
    localStorage.removeItem("papertiger_session");
  }

  /**
   * Registers a new account.
   *
   * @param email Account email.
   * @param password Account password.
   * @param eligibilityAcknowledged Required 13+ and parental-permission acknowledgement.
   * @returns Server message.
   */
  public async register(
    email: string,
    password: string,
    eligibilityAcknowledged: true,
  ): Promise<{ message: string }> {
    return this.call("/v1/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, password, eligibilityAcknowledged }),
    });
  }

  /**
   * Confirms an email and persists the returned session.
   *
   * @param token Verification token from the URL.
   * @returns Confirmed user.
   */
  public async confirmEmail(token: string): Promise<UserSummary> {
    const response = await this.call<{ user: UserSummary }>(
      "/v1/auth/confirm-email",
      { method: "POST", body: JSON.stringify({ token }) },
    );
    return response.user;
  }

  /**
   * Logs in and persists the returned session.
   *
   * @param email Account email.
   * @param password Account password.
   * @returns Authenticated user.
   */
  public async login(email: string, password: string): Promise<UserSummary> {
    const response = await this.call<{ user: UserSummary }>(
      "/v1/auth/login",
      { method: "POST", body: JSON.stringify({ email, password }) },
    );
    return response.user;
  }

  /**
   * Logs out and clears the local session.
   *
   * @returns Nothing.
   */
  public async logout(): Promise<void> {
    try {
      await this.call("/v1/auth/logout", { method: "POST" });
    } finally {
      this.setToken(null);
    }
  }

  /**
   * Loads the current user.
   *
   * @returns Authenticated user.
   */
  public async me(): Promise<UserSummary> {
    const response = await this.call<{ user: UserSummary }>("/v1/me");
    return response.user;
  }

  /** Ends the current browser account's active trial immediately. */
  public async cancelTrial(): Promise<UserSummary> {
    const response = await this.call<{ user: UserSummary }>(
      "/v1/entitlements/trial/cancel",
      { method: "POST" },
    );
    return response.user;
  }

  /** Changes the current user's password without allowing an email-address change. */
  public async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.call("/v1/me/password", {
      method: "PATCH",
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  /** Permanently deletes the current user's account and all owned application data. */
  public async deleteAccount(password: string): Promise<void> {
    await this.call("/v1/me", {
      method: "DELETE",
      body: JSON.stringify({ password }),
    });
    this.setToken(null);
  }

  /**
   * Loads the current application-wide operational limits.
   *
   * @returns Application settings available to the authenticated user.
   */
  public async applicationSettings(): Promise<ApplicationSettings> {
    return this.call("/v1/admin/settings");
  }

  /**
   * Replaces an administrator-managed application limit.
   *
   * @param settings Replacement application settings.
   * @returns Persisted application settings.
   */
  public async updateApplicationSettings(
    settings: ApplicationSettings,
  ): Promise<ApplicationSettings> {
    return this.call("/v1/admin/settings", {
      method: "PATCH",
      body: JSON.stringify(settings),
    });
  }

  /** Lists all accounts for the administrator workspace. */
  public async adminUsers(): Promise<AdminUserSummary[]> {
    return (await this.call<{ users: AdminUserSummary[] }>("/v1/admin/users")).users;
  }

  /** Creates an email-confirmation-bound account from the administrator workspace. */
  public async createAdminUser(input: {
    email: string;
    password: string;
    role: "admin" | "user";
  }): Promise<UserSummary> {
    return (await this.call<{ user: UserSummary }>("/v1/admin/users", {
      method: "POST",
      body: JSON.stringify(input),
    })).user;
  }

  /** Updates a managed account's role and/or password. */
  public async updateAdminUser(
    userId: string,
    input: { password?: string; role?: "admin" | "user" },
  ): Promise<AdminUserSummary> {
    return this.call(`/v1/admin/users/${encodeURIComponent(userId)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  }

  /** Permanently deletes a managed account. */
  public async deleteAdminUser(userId: string): Promise<void> {
    await this.call(`/v1/admin/users/${encodeURIComponent(userId)}`, { method: "DELETE" });
  }

  /** Starts a hosted Stripe Checkout session for the current browser account. */
  public async startCheckout(): Promise<string> {
    return (await this.call<{ url: string }>("/v1/billing/checkout", { method: "POST" })).url;
  }

  /** Opens Stripe's hosted Billing Portal for the current browser account. */
  public async billingPortal(): Promise<string> {
    return (await this.call<{ url: string }>("/v1/billing/portal", { method: "POST" })).url;
  }

  /**
   * Sends a public support or feedback message.
   *
   * @param contact Validated contact-form fields.
   * @returns Server acknowledgement.
   */
  public async contact(contact: ContactRequest): Promise<{
    submissionId?: string;
    message: string;
  }> {
    return this.call("/v1/contact", {
      method: "POST",
      body: JSON.stringify(contact),
    });
  }

  /**
   * Lists account wallets.
   *
   * @returns Wallet list.
   */
  public async wallets(): Promise<WalletListItem[]> {
    const response = await this.call<{ wallets: WalletListItem[] }>("/v1/wallets");
    return response.wallets;
  }

  /**
   * Creates a paper wallet.
   *
   * @param name Wallet name.
   * @param startingBalanceDollars Initial balance.
   * @returns Created wallet.
   */
  public async createWallet(
    name: string,
    startingBalanceDollars: number,
  ): Promise<WalletListItem> {
    const response = await this.call<{ wallet: WalletListItem }>("/v1/wallets", {
      method: "POST",
      body: JSON.stringify({ name, startingBalanceDollars }),
    });
    return response.wallet;
  }

  /**
   * Resets an owned wallet.
   *
   * @param walletId Wallet identifier.
   * @returns Nothing.
   */
  public async resetWallet(walletId: string): Promise<void> {
    await this.call(`/v1/wallets/${walletId}/reset`, { method: "POST" });
  }

  /**
   * Loads marked wallet equity and positions.
   *
   * @param walletId Wallet identifier.
   * @returns Marked portfolio.
   */
  public async portfolio(walletId: string): Promise<PortfolioResponse> {
    return this.call(`/v1/wallets/${walletId}/portfolio`);
  }

  /**
   * Searches prediction markets.
   *
   * @param query Search text.
   * @param venue Venue filter.
   * @returns Markets and non-fatal venue warnings.
   */
  public async searchMarkets(
    query: string,
    venue: "all" | "polymarket" | "kalshi",
  ): Promise<{ markets: PredictionMarket[]; warnings: string[] }> {
    const params = new URLSearchParams({ q: query, venue });
    return this.call(`/v1/markets/search?${params.toString()}`);
  }

  /**
   * Loads liquid markets with materially moving odds for the discovery screen.
   *
   * @returns Suggested markets and non-fatal venue warnings.
   */
  public async suggestedMarkets(): Promise<{
    markets: SuggestedMarket[];
    warnings: string[];
  }> {
    return this.call("/v1/markets/suggestions");
  }

  /**
   * Resolves a supported public Polymarket or Kalshi market URL.
   *
   * @param marketUrl Source venue market URL.
   * @returns Matching live markets and non-fatal venue warnings.
   */
  public async resolveMarketUrl(marketUrl: string): Promise<{
    markets: PredictionMarket[];
    warnings: string[];
  }> {
    const params = new URLSearchParams({ url: marketUrl });
    return this.call(`/v1/markets/resolve?${params.toString()}`);
  }

  /**
   * Loads the live public order book for one tradable outcome.
   *
   * @param venue Venue identifier.
   * @param marketId Canonical venue market identifier.
   * @param outcomeId Canonical venue outcome identifier.
   * @returns Current normalized order book.
   */
  public async orderBook(
    venue: "polymarket" | "kalshi",
    marketId: string,
    outcomeId: string,
  ): Promise<OrderBook> {
    const path = [
      "/v1/markets",
      encodeURIComponent(venue),
      encodeURIComponent(marketId),
      "outcomes",
      encodeURIComponent(outcomeId),
      "book",
    ].join("/");
    return this.call(path);
  }

  /**
   * Places a simulated market order.
   *
   * @param order Validated order payload.
   * @returns Persisted order.
   */
  public async placeOrder(order: PlaceOrderRequest): Promise<OrderSummary> {
    const response = await this.call<{ order: OrderSummary }>("/v1/orders", {
      method: "POST",
      headers: { "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify(order),
    });
    return response.order;
  }

  /**
   * Lists recent wallet orders.
   *
   * @param walletId Wallet identifier.
   * @param status Open, closed, or every order.
   * @returns Current order page.
   */
  public async orders(
    walletId: string,
    status: "open" | "closed" | "all" = "all",
  ): Promise<{ orders: OrderSummary[]; nextCursor: string | null }> {
    const params = new URLSearchParams({ walletId, status, limit: "100" });
    return this.call(`/v1/orders?${params.toString()}`);
  }

  /**
   * Cancels one accepted order.
   *
   * @param orderId Order identifier.
   * @returns Current order.
   */
  public async cancelOrder(orderId: string): Promise<OrderSummary> {
    const response = await this.call<{ order: OrderSummary }>(
      `/v1/orders/${orderId}`,
      { method: "DELETE" },
    );
    return response.order;
  }

  /**
   * Loads wallet equity replayed from immutable fills and settlements.
   *
   * @param walletId Wallet identifier.
   * @param query Optional time window and observation limit.
   * @returns Chronological equity points.
   */
  public async portfolioHistory(
    walletId: string,
    query: PortfolioHistoryQuery = {},
  ): Promise<PortfolioHistoryPoint[]> {
    const params = new URLSearchParams();
    if (query.start !== undefined) params.set("start", query.start);
    if (query.end !== undefined) params.set("end", query.end);
    if (query.limit !== undefined) params.set("limit", `${query.limit}`);
    const queryString = params.size === 0 ? "" : `?${params.toString()}`;
    const response = await this.call<{ points: PortfolioHistoryPoint[] }>(
      `/v1/wallets/${walletId}/portfolio/history${queryString}`,
    );
    return response.points;
  }

  /**
   * Loads recent wallet cash-changing activities.
   *
   * @param walletId Wallet identifier.
   * @returns Recent activity page.
   */
  public async activities(walletId: string): Promise<AccountActivitySummary[]> {
    const response = await this.call<{
      activities: AccountActivitySummary[];
      nextCursor: string | null;
    }>(`/v1/wallets/${walletId}/activities?limit=50`);
    return response.activities;
  }

  /**
   * Lists active API key metadata.
   *
   * @returns API keys.
   */
  public async apiKeys(): Promise<ApiKeySummary[]> {
    const response = await this.call<{ apiKeys: ApiKeySummary[] }>("/v1/api-keys");
    return response.apiKeys;
  }

  /**
   * Creates an API key whose secret is returned once.
   *
   * @param name Key name.
   * @param walletId Selected default wallet identifier.
   * @returns Key metadata and raw token.
   */
  public async createApiKey(
    name: string,
    walletId: string,
  ): Promise<{ apiKey: ApiKeySummary; key: string; message: string }> {
    return this.call("/v1/api-keys", {
      method: "POST",
      body: JSON.stringify({ name, walletId }),
    });
  }

  /**
   * Changes an API key's default wallet or wallet-access scope.
   *
   * @param apiKeyId Key identifier.
   * @param input Wallet configuration changes.
   * @returns Updated key metadata.
   */
  public async updateApiKey(
    apiKeyId: string,
    input: UpdateApiKeyRequest,
  ): Promise<ApiKeySummary> {
    const response = await this.call<{ apiKey: ApiKeySummary }>(
      `/v1/api-keys/${apiKeyId}`,
      {
        method: "PATCH",
        body: JSON.stringify(input),
      },
    );
    return response.apiKey;
  }

  /**
   * Revokes an API key.
   *
   * @param apiKeyId Key identifier.
   * @returns Nothing.
   */
  public async revokeApiKey(apiKeyId: string): Promise<void> {
    await this.call(`/v1/api-keys/${apiKeyId}`, { method: "DELETE" });
  }

  /**
   * Converts a natural-language idea into an editable strategy form.
   *
   * @param prompt User's strategy idea.
   * @returns Validated strategy and provider usage.
   */
  public async generateStrategy(prompt: string): Promise<{
    strategy: StrategyDefinitionV2;
    usage: StrategyGenerationUsage;
  }> {
    return this.call("/v1/strategies/generate", {
      method: "POST",
      body: JSON.stringify({ prompt }),
    });
  }

  /**
   * Runs and persists one bounded historical backtest.
   *
   * @param request Strategy, time range, and starting cash.
   * @returns Completed deterministic result.
   */
  public async runBacktest(
    request: CreateBacktestRequest,
  ): Promise<BacktestResult> {
    const response = await this.call<{ result: BacktestResult }>(
      "/v1/backtests",
      {
        method: "POST",
        body: JSON.stringify(request),
      },
    );
    return response.result;
  }

  /**
   * Loads the current historical market-data coverage index.
   *
   * @returns Replay ranges, fidelity, freshness, and known gaps.
   */
  public async getMarketDataCoverage(): Promise<MarketDataCoverageResponse> {
    return this.call("/v1/market-data/coverage");
  }

  /**
   * Calls the API and turns error payloads into readable errors.
   *
   * @param path API path.
   * @param options Fetch options.
   * @returns Parsed response.
   */
  private async call<T>(path: string, options: RequestInit = {}): Promise<T> {
    const headers = new Headers(options.headers);
    if (options.body !== undefined) {
      headers.set("Content-Type", "application/json");
    }
    const response = await fetch(`${API_BASE_URL}${path}`, { ...options, headers, credentials: "include" });
    if (response.status === 204) {
      return undefined as T;
    }
    const body = (await response.json()) as T & { message?: string };
    if (!response.ok) {
      if (response.status === 401 && !path.includes("/auth/")) {
        this.setToken(null);
      }
      throw new Error(body.message ?? `Request failed with ${response.status}.`);
    }
    return body;
  }
}

/** Shared browser API client. */
export const api = new PaperTigerApi();
